import { Fragment, useEffect, useState, useContext, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import AttendeeCard from './AttendeeCard';
import AuthContext from '../../../../auth/AuthContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import QuizService from '../../../../services/QuizService';

const QuizAttendees = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const { setDynamicLabels } = useBreadcrumb();
  const [loading, setLoading] = useState(true);
  const [attendees, setAttendees] = useState([]);

  useEffect(() => {
    const quizName = location.state?.quizName;

    if (quizName) {
      setDynamicLabels((prev) => ({
        ...prev,
        [id]: quizName,
      }));
    }
  }, [id, location.state, setDynamicLabels]);

  useEffect(() => {
    const quizAccess = authContext.getPermissions('quizmaster');
    if (quizAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate('/');
    }
  }, [authContext, navigate]);

  const fetchAttendees = useCallback(async () => {
    try {
      setLoading(true);

      const res = await QuizService.GetQuizAttendees(
        50,
        0,
        id,
        '',
        authContext
      );

      const list = res?.data?.results || res?.data?.data?.results || [];

      const mapped = list.map((u) => {
        const percentage = u.totalQuestions
          ? Math.round((u.correctAnswers / u.totalQuestions) * 100)
          : 0;

        return {
          id: u.id,
          userId: u.userId,
          attendeeName: u.attendeeName || 'Unknown',
          gender: u.gender,
          isExamCompleted: u.isExamCompleted,

          correctAnswers: u.correctAnswers,
          graceMarks: u.graceMarks ?? 0,
          totalQuestions: u.totalQuestions,
          percentage,
        };
      });

      setAttendees(mapped);
    } catch (err) {
      console.error('Failed to fetch quiz attendees', err);
      setAttendees([]);
    } finally {
      setLoading(false);
    }
  }, [id, authContext]);

  const onCardClick = (attendeeId) => {
    navigate(`/masters-quizzes/${id}/attendees/${attendeeId}`);
  };

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  return (
    <Fragment>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-3">
        {loading
          ? Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                className="
                  bg-white dark:bg-slate-900
                  rounded-xl shadow p-4
                "
              >
                <div className="flex items-center gap-4">
                  <Skeleton
                    shape="circle"
                    size="6rem"
                    className="!bg-slate-200 dark:!bg-slate-700"
                  />

                  <div className="flex-1 space-y-3">
                    <Skeleton
                      width="70%"
                      height="1.1rem"
                      className="!bg-slate-200 dark:!bg-slate-700"
                    />
                    <Skeleton
                      width="90%"
                      height="0.9rem"
                      className="!bg-slate-200 dark:!bg-slate-700"
                    />
                    <Skeleton
                      width="80%"
                      height="0.9rem"
                      className="!bg-slate-200 dark:!bg-slate-700"
                    />
                  </div>

                  <Skeleton
                    shape="circle"
                    size="5.5rem"
                    className="!bg-slate-200 dark:!bg-slate-700"
                  />
                </div>
              </div>
            ))
          : attendees.map((attendee) => (
              <AttendeeCard
                key={attendee.id}
                id={attendee.id}
                data={attendee}
                clickHandler={onCardClick}
              />
            ))}
      </div>

      {!loading && attendees.length === 0 && (
        <div className="text-center text-gray-500 mt-10">
          No data available...
        </div>
      )}
    </Fragment>
  );
};

export default QuizAttendees;
